import { useState } from "react";
import { BarChart3, Braces, Layers, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { usePoll } from "@/hooks/usePoll";
import { api, errMsg, get } from "@/lib/api";
import { fmt, fmtChg, fmtPct, fmtQty, fmtTime, tone } from "@/lib/format";
import { LtpCell } from "./LtpCell";
import { StatusBadge } from "./StatusBadge";
import { DepthPanel } from "./DepthPanel";
import { CandlesPanel } from "./CandlesPanel";

import { useLiveOne } from "./LiveProvider";

const Field = ({ label, value, testid }) => (
  <div className="rounded border border-slate-800 bg-slate-950/40 px-2.5 py-1.5">
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div data-testid={testid} className="num text-[13px] text-slate-200">{value}</div>
  </div>
);

const Tab = ({ id, cur, set, icon: Icon, children }) => (
  <Button data-testid={`symbol-tab-${id}`} size="sm" variant="outline" onClick={() => set(cur === id ? null : id)}
    className={`h-8 border-slate-700 bg-slate-900 text-xs ${cur === id ? "border-cyan-600 text-cyan-300" : "text-slate-300"}`}>
    <Icon className="mr-1.5 h-3.5 w-3.5" /> {children}
  </Button>
);

const Body = ({ sid, onClose }) => {
  const [panel, setPanel] = useState(null);
  const { data: polled, error } = usePoll(() => get(`/symbols/${sid}/snapshot`), 2000, [sid]);
  const liveSnap = useLiveOne(sid);
  if (error) return <p data-testid="snapshot-error" className="p-4 text-sm text-rose-400">{errMsg(error)}</p>;
  if (!polled) return <div className="m-4 h-48 animate-pulse rounded bg-slate-900" />;
  const s = liveSnap && liveSnap.instrument_token === polled.instrument_token ? { ...polled, ...liveSnap } : polled;
  const unsub = async () => {
    try {
      const { data } = await api.post(`/symbols/${sid}/unsubscribe`);
      toast.success(data.still_subscribed_for?.length ? `Kept: also used by ${data.still_subscribed_for.join(", ")}` : `Unsubscribed ${s.tradingsymbol}`);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const isIdx = s.cls === "index";
  return (
    <div className="space-y-4 px-1 pb-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <LtpCell value={s.ltp} testid="snapshot-ltp" className="text-3xl font-semibold text-slate-50" />
          <div className={`num mt-1 text-sm font-semibold ${tone(s.change)}`} data-testid="snapshot-change">
            {fmtChg(s.change)} ({fmtPct(s.change_pct)})
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusBadge status={s.status} testid="snapshot-status" />
          <span className="font-mono text-[10px] text-slate-500">updated {fmtTime(s.last_update)} · age {s.age_seconds ?? "—"}s</span>
        </div>
      </div>
      {s.subscribe_error && <p className="text-xs text-amber-400" data-testid="snapshot-subscribe-error">{s.subscribe_error}</p>}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Field label="Open" value={fmt(s.ohlc?.open)} testid="snap-open" />
        <Field label="High" value={fmt(s.ohlc?.high)} testid="snap-high" />
        <Field label="Low" value={fmt(s.ohlc?.low)} testid="snap-low" />
        <Field label="Prev close" value={fmt(s.ohlc?.close)} testid="snap-close" />
        {!isIdx && <Field label="Volume" value={fmtQty(s.volume)} testid="snap-volume" />}
        {!isIdx && <Field label="Open interest" value={fmtQty(s.oi)} testid="snap-oi" />}
        {!isIdx && <Field label="Avg price" value={fmt(s.avg_price)} testid="snap-avg" />}
        {!isIdx && <Field label="Bid / Ask" value={s.best_bid ? `${fmt(s.best_bid)} / ${fmt(s.best_ask)}` : "—"} testid="snap-bidask" />}
        {!isIdx && <Field label="Buy / Sell qty" value={`${fmtQty(s.total_buy_qty)} / ${fmtQty(s.total_sell_qty)}`} testid="snap-buysell" />}
        <Field label="Prev tick" value={s.previous ? `${fmt(s.previous.ltp)} @ ${fmtTime(s.previous.last_update)}` : "—"} testid="snap-prev" />
        <Field label="Market" value={String(s.market_status).toUpperCase()} testid="snap-market" />
        <Field label="Subscribed for" value={s.reasons?.join(", ") || "—"} testid="snap-reasons" />
      </div>
      <div className="flex flex-wrap gap-2">
        {!isIdx && <Tab id="depth" cur={panel} set={setPanel} icon={Layers}>Market depth</Tab>}
        <Tab id="candles" cur={panel} set={setPanel} icon={BarChart3}>Candles</Tab>
        <Tab id="raw" cur={panel} set={setPanel} icon={Braces}>Raw JSON</Tab>
        {s.reasons?.some((r) => r === "on_demand" || r === "option_chain") && (
          <Button data-testid="unsubscribe-button" size="sm" variant="ghost" onClick={unsub} className="ml-auto h-8 text-xs text-rose-400 hover:bg-rose-950/40 hover:text-rose-300">
            <X className="mr-1 h-3.5 w-3.5" /> Unsubscribe
          </Button>
        )}
      </div>
      {panel === "depth" && <DepthPanel sid={sid} />}
      {panel === "candles" && <CandlesPanel sid={sid} />}
      {panel === "raw" && (
        <pre data-testid="snapshot-raw-json" className="scroll-thin max-h-80 overflow-auto rounded border border-slate-800 bg-slate-950 p-3 font-mono text-[11px] text-slate-400">
          {JSON.stringify(s, null, 2)}
        </pre>
      )}
    </div>
  );
};

export const SymbolSheet = ({ sym, onClose }) => (
  <Sheet open={!!sym} onOpenChange={(o) => !o && onClose()}>
    <SheetContent data-testid="symbol-sheet" side="right" className="scroll-thin w-full overflow-y-auto border-slate-800 bg-[#0E1420] sm:max-w-2xl">
      {sym && (
        <>
          <SheetHeader className="mb-4 text-left">
            <SheetTitle data-testid="symbol-sheet-title" className="font-mono text-xl text-slate-100">{sym.tradingsymbol}</SheetTitle>
            <SheetDescription className="font-mono text-[11px] text-slate-500">
              {[sym.exchange, sym.cls, `token ${sym.instrument_token}`, sym.expiry && `exp ${sym.expiry}`, sym.lot_size > 1 && `lot ${sym.lot_size}`]
                .filter(Boolean).join(" · ")}
            </SheetDescription>
          </SheetHeader>
          <Body sid={sym.instrument_token} onClose={onClose} />
        </>
      )}
    </SheetContent>
  </Sheet>
);
